/**
 * The chart tools an agent can be given, one key per tool. The `available_widgets` feature stores a
 * subset of these keys per agent, so the keys are persisted: adding one is safe, renaming or removing
 * one needs a data migration (see `LEGACY_AGENT_CHART_TOOL_KEYS`).
 *
 * The feature's valid values and option labels derive from this record, and chat-api's tool map is
 * keyed by it, so a key without a tool (or a tool without a key) is a compile error.
 *
 * Why one key per tool: the bar tool lets the model choose `bar` or `stacked-bar`, and the multiple-bar
 * tool lets it choose clustered or stacked, so enabling one variant always enabled the other. The
 * earlier separate keys implied a choice that was never enforced.
 */
export const AGENT_CHART_TOOL_KEYS = ['kpi-card', 'bar', 'line', 'multiple-line', 'multiple-bar', 'pie'];
export const AGENT_CHART_TOOLS = {
    'kpi-card': { displayName: 'KPI Card', produces: ['metric_card'] },
    bar: { displayName: 'Bar Chart (plain or stacked)', produces: ['bar', 'stacked-bar'] },
    line: { displayName: 'Line Chart', produces: ['multiple-line'] },
    'multiple-line': { displayName: 'Multiple Line Chart', produces: ['multiple-line'] },
    'multiple-bar': {
        displayName: 'Multiple Bar Chart (clustered or stacked)',
        produces: ['multiple-clustered-bar', 'multiple-stacked-bar'],
    },
    pie: { displayName: 'Pie Chart', produces: ['pie'] },
};
/** The option metadata the multi-select feature shows for each key. */
export const AGENT_CHART_TOOL_OPTIONS = Object.fromEntries(AGENT_CHART_TOOL_KEYS.map((key) => [key, { displayName: AGENT_CHART_TOOLS[key].displayName }]));
/**
 * Keys that used to be valid and what they became. Stored values are rewritten by the
 * `merge_agent_chart_tool_keys` migration; this keeps code working against rows it hasn't reached yet.
 */
export const LEGACY_AGENT_CHART_TOOL_KEYS = {
    'stacked-bar': 'bar',
    'multiple-clustered-bar': 'multiple-bar',
    'multiple-stacked-bar': 'multiple-bar',
};
/** The keys with legacy ones replaced by their current key, without duplicates, in first-seen order. Unknown values pass through. */
export function normalizeAgentChartToolKeys(values) {
    const normalized = [];
    for (const value of values) {
        const key = LEGACY_AGENT_CHART_TOOL_KEYS[value] ?? value;
        if (!normalized.includes(key))
            normalized.push(key);
    }
    return normalized;
}
